/**
 * useVideoProviderAvailability hook
 *
 * Fetches GET /api/v1/video-providers/availability once and caches the result
 * for CACHE_TTL_MS (30 s). Exposes a Map<id, VideoProviderAvailability> for
 * O(1) look-ups in AIVideoProviderPanel and other consumers.
 *
 * Design mirrors useComponentStatus.ts: transient errors do not clear the last
 * known good state so the UI does not flicker.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/services/apiClient';
import type {
  VideoProviderAvailability,
  VideoProvidersAvailabilityResponse,
} from '@/types/videoProviders';

/** In-process module-level cache so multiple hook instances share one fetch. */
let _moduleCache: Map<string, VideoProviderAvailability> | null = null;
let _moduleCacheTs = 0;
const CACHE_TTL_MS = 30_000;

export interface UseVideoProviderAvailabilityResult {
  /** Map from provider id to its availability record, null while loading. */
  availability: Map<string, VideoProviderAvailability> | null;
  isLoading: boolean;
  error: string | null;
  /** Manually invalidate the cache and re-fetch. */
  refresh: () => void;
}

export function useVideoProviderAvailability(): UseVideoProviderAvailabilityResult {
  const [availability, setAvailability] = useState<Map<string, VideoProviderAvailability> | null>(
    _moduleCache
  );
  const [isLoading, setIsLoading] = useState<boolean>(_moduleCache === null);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = useCallback((): void => {
    _moduleCache = null;
    _moduleCacheTs = 0;
    setRefreshToken((n) => n + 1);
  }, []);

  useEffect(() => {
    cancelledRef.current = false;

    const now = Date.now();
    if (_moduleCache !== null && now - _moduleCacheTs < CACHE_TTL_MS) {
      setAvailability(_moduleCache);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const fetchAvailability = async (): Promise<void> => {
      try {
        const res = await apiClient.get<VideoProvidersAvailabilityResponse>(
          '/api/v1/video-providers/availability'
        );
        if (cancelledRef.current) return;

        const map = new Map<string, VideoProviderAvailability>();
        for (const provider of res.data.providers) {
          map.set(provider.id, provider);
        }

        _moduleCache = map;
        _moduleCacheTs = Date.now();

        setAvailability(map);
        setIsLoading(false);
        setError(null);
      } catch (err: unknown) {
        if (cancelledRef.current) return;
        // Keep last-known-good state; only update error text.
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        setIsLoading(false);
      }
    };

    void fetchAvailability();

    return () => {
      cancelledRef.current = true;
    };
  }, [refreshToken]);

  return { availability, isLoading, error, refresh };
}
